import { useState } from 'react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { JournalBody } from './JournalBody';
import { CandidateBotLine } from './CandidateBotLine';
import { useSaveNote } from '@/hooks/queries/useJournal';
import { formatDateTime } from '@/lib/formatters';
import type { JournalEntry } from '@steward/shared';

const NOTE_BADGE = { label: 'Note', className: 'bg-gray-100 text-gray-700' };

const BADGES: Record<string, { label: string; className: string }> = {
  opened: { label: 'Opened', className: 'bg-sky-100 text-sky-800' },
  rolled: { label: 'Rolled', className: 'bg-violet-100 text-violet-800' },
  closed: { label: 'Closed', className: 'bg-gray-100 text-gray-700' },
  expired: { label: 'Expired', className: 'bg-green-100 text-green-800' },
  assigned: { label: 'Assigned', className: 'bg-amber-100 text-amber-800' },
  exercised: { label: 'Exercised', className: 'bg-amber-100 text-amber-800' },
};

export function JournalEntryCard({ entry }: { entry: JournalEntry }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(entry.note ?? '');
  const saveNote = useSaveNote();
  const badge = entry.source === 'user' ? NOTE_BADGE : (entry.eventType && BADGES[entry.eventType]) || NOTE_BADGE;
  const ind = entry.indicators ?? {};

  return (
    <div className="py-3 border-b border-gray-100 last:border-0">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Badge className={badge.className}>{badge.label}</Badge>
        <span className="text-xs text-gray-400">{formatDateTime(entry.occurredAt)}</span>
      </div>
      <p className="text-sm text-gray-800 mt-1 break-words">
        <JournalBody body={entry.body} />
      </p>
      {(ind.dte != null || ind.shortPutDistancePct != null || ind.shortCallDistancePct != null) && (
        <div className="flex gap-2 mt-1 flex-wrap">
          {ind.dte != null && (
            <span className="text-xs bg-gray-50 text-gray-600 px-1.5 py-0.5 rounded">{ind.dte} DTE</span>
          )}
          {ind.shortPutDistancePct != null && (
            <span className="text-xs bg-gray-50 text-gray-600 px-1.5 py-0.5 rounded">
              put {ind.shortPutDistancePct.toFixed(1)}% OTM
            </span>
          )}
          {ind.shortCallDistancePct != null && (
            <span className="text-xs bg-gray-50 text-gray-600 px-1.5 py-0.5 rounded">
              call {ind.shortCallDistancePct.toFixed(1)}% OTM
            </span>
          )}
        </div>
      )}
      {/* `realized` is null here by design: this container has no chain P&L and fetching one per
          entry would be a query per card. The bot side still renders. */}
      <CandidateBotLine link={entry.candidate ?? null} eventType={entry.eventType} realized={null} />
      {editing ? (
        <div className="mt-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={2000}
            rows={2}
            className="w-full text-sm border border-gray-200 rounded p-2"
            placeholder="Why? What were you seeing?"
          />
          <div className="flex gap-2 mt-1">
            <Button
              size="sm"
              onClick={() => {
                saveNote.mutate(
                  { id: entry.id, note: draft.trim() || null },
                  {
                    onSuccess: () => setEditing(false),
                    onError: () => {/* keep editor open, draft intact */},
                  },
                );
              }}
            >
              Save
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setDraft(entry.note ?? '');
                setEditing(false);
              }}
            >
              Cancel
            </Button>
          </div>
          {saveNote.isError && (
            <p className="text-xs text-red-600 mt-1">Couldn&apos;t save — try again.</p>
          )}
        </div>
      ) : entry.note ? (
        <p
          className="text-sm text-gray-600 italic mt-1 cursor-pointer break-words"
          onClick={() => {
            setDraft(entry.note ?? '');
            setEditing(true);
          }}
        >
          &ldquo;{entry.note}&rdquo;
        </p>
      ) : entry.source === 'auto' ? (
        <button
          className="text-xs text-gray-400 hover:text-gray-600 mt-1"
          onClick={() => {
            setDraft('');
            setEditing(true);
          }}
        >
          + Add note
        </button>
      ) : null}
    </div>
  );
}
